// Bell rows for inbound calls (CU-869fmcc9r). Dependency-free on purpose: the
// mapping is unit-tested without the components barrel.
//
// One notification per call, updated in place by the backend:
//   incoming_call (ringing) -> answered_call | missed_call
// `data` = { call_id, state, number (E.164), lead_id, lead_name, ringing_at: [desk names] }.
// Older "missed_call" rows were created without the ringing row and may lack
// `data`; fall back to action_object / description.

import { formatUkPhone } from '../../utils/ukPhone';

export const CALL_VERBS = Object.freeze(['incoming_call', 'answered_call', 'missed_call']);
export const isCallVerb = (verb) => CALL_VERBS.includes(verb);

export const CALL_STATE = Object.freeze({ RINGING: 'ringing', ANSWERED: 'answered', MISSED: 'missed' });

export const CALL_ACTION = Object.freeze({ OPEN_LEAD: 'open-lead', CREATE_LEAD: 'create-lead', CALL_BACK: 'call-back' });

// verb -> notification type key (see notificationTypes.js)
export const CALL_VERB_TO_TYPE = Object.freeze({
  incoming_call: 'CALL_RINGING',
  answered_call: 'CALL_ANSWERED',
  missed_call: 'CALL_MISSED',
});

const VERB_STATE = { incoming_call: CALL_STATE.RINGING, answered_call: CALL_STATE.ANSWERED, missed_call: CALL_STATE.MISSED };

const str = (v) => (v === null || v === undefined ? '' : String(v).trim());
const objectOf = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});

// The verb is authoritative (the backend rewrites it on every state change);
// data.state is only used when the verb is not a call verb.
export function callStateOf(raw) {
  const verbState = VERB_STATE[raw?.verb];
  if (verbState) return verbState;
  const s = str(objectOf(raw?.data).state).toLowerCase();
  return Object.values(CALL_STATE).includes(s) ? s : null;
}

const PHONE_IN_TEXT = /(\+?\d[\d\s()-]{6,}\d)/;

// Plain facts out of a raw notification; every field tolerant of absence.
export function callFacts(raw) {
  const data = objectOf(raw?.data);
  const obj = objectOf(raw?.action_object);
  const objLead = objectOf(obj.lead);
  const callId = str(data.call_id ?? data.callId ?? obj.call_id ?? obj.id);
  const leadId = data.lead_id ?? data.leadId ?? objLead.id ?? obj.lead_id ?? null;
  const leadName = str(data.lead_name ?? data.leadName ?? objLead.name ?? obj.lead_name);
  let number = str(data.number ?? obj.number ?? obj.from_number ?? obj.caller_number ?? obj.contact);
  // An explicit empty data.number means withheld: do not dig a number out of the text.
  if (!number && !('number' in data)) {
    const m = str(raw?.description).match(PHONE_IN_TEXT);
    if (m) number = m[1].trim();
  }
  const ringingAt = (Array.isArray(data.ringing_at) ? data.ringing_at : []).map(str).filter(Boolean);
  return {
    callId,
    leadId: leadId === '' ? null : leadId,
    leadName,
    number,
    ringingAt,
  };
}

const STATE_LABEL = {
  [CALL_STATE.RINGING]: 'Ringing now',
  [CALL_STATE.MISSED]: 'Missed call',
  [CALL_STATE.ANSWERED]: 'Answered',
};

// Everything a bell row needs for a call notification, or null for other verbs.
export function callNotificationView(raw) {
  if (!raw || !isCallVerb(raw.verb)) return null;
  const state = callStateOf(raw);
  const { callId, leadId, leadName, number, ringingAt } = callFacts(raw);
  const hasLead = leadId !== null && leadId !== undefined;
  const numberText = number ? formatUkPhone(number) : '';
  const who = leadName || numberText || 'Unknown caller';

  const actions = [];
  if (hasLead) actions.push({ key: CALL_ACTION.OPEN_LEAD, label: 'Open lead' });
  else if (number) actions.push({ key: CALL_ACTION.CREATE_LEAD, label: 'Create lead' });
  // Call back needs a real number; a withheld caller has none.
  if (state === CALL_STATE.MISSED && number) actions.push({ key: CALL_ACTION.CALL_BACK, label: 'Call back' });

  let message = `${STATE_LABEL[state] || 'Call'} · ${who}`;
  if (hasLead && leadName && numberText) message += ` · ${numberText}`;
  if (state === CALL_STATE.RINGING && ringingAt.length) message += ` · ringing at ${ringingAt.join(', ')}`;

  return { state, callId, leadId: hasLead ? leadId : null, leadName, number, numberText, ringingAt, who, actions, message, stateLabel: STATE_LABEL[state] || '' };
}

// The Call Logs row shape the call flow accepts (callFlowModel normaliseCall -> fromRow).
export function callFlowRow(view) {
  if (!view || !view.callId) return null;
  return {
    id: `voice:${view.callId}`,
    contact: view.number || '',
    direction: 'inbound',
    outcome: view.state === CALL_STATE.MISSED ? 'missed' : '',
    callerLabel: '',
    lead: view.leadId !== null && view.leadId !== undefined ? { id: view.leadId, name: view.leadName } : null,
    linkEarlier: false,
  };
}

// Calls never toast: the call pill is the live surface, the bell just refreshes.
export const shouldToastVerb = (verb) => !isCallVerb(verb);
